import type { CallOutlineSegment } from "@repo/app-types/entities/teammateAccountCallOutline";
import { useLayoutEffect, useRef, type FC } from "react";

export interface CallKeyMomentRailRow {
  segment: CallOutlineSegment;
  top: number;
  height: number;
}

interface CallKeyMomentThumb {
  top: number;
  height: number;
  scale: number;
}

const THUMB_INSET_PX = 4;
const GAP_THUMB_HEIGHT_PX = 3;
const GAP_THUMB_SCALE = 2;
const ANIMATION_STAGE_DURATION_MS = 200;
const ANIMATION_STAGGER_MS = 100;

interface CallKeyMomentThumbAnimation {
  frames: Keyframe[];
  duration: number;
}

interface TranscriptKeyMomentThumbProps {
  thumb: CallKeyMomentThumb;
}

export const TranscriptKeyMomentThumb: FC<TranscriptKeyMomentThumbProps> = (
  props,
) => {
  const { thumb } = props;
  const elementRef = useRef<HTMLSpanElement>(null);
  const animationRef = useRef<Animation | null>(null);
  const isInitializedRef = useRef(false);
  const { top, height, scale } = thumb;

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    const target = { top, height, scale };
    const shouldAnimate =
      isInitializedRef.current &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    isInitializedRef.current = true;

    // Capture the visible frame before canceling so a seek does not jump.
    let current: CallKeyMomentThumb | undefined;
    if (shouldAnimate) {
      const style = getComputedStyle(element);
      current = {
        top: Number.parseFloat(style.top),
        height: Number.parseFloat(style.height),
        scale: new DOMMatrixReadOnly(
          style.transform === "none" ? undefined : style.transform,
        ).a,
      };
    }
    animationRef.current?.cancel();
    animationRef.current = null;
    Object.assign(element.style, renderCallKeyMomentThumbFrame(target));
    if (!current) return;

    const { frames, duration } = buildCallKeyMomentThumbTransition(
      current,
      target,
    );
    animationRef.current = element.animate(frames, { duration });
  }, [top, height, scale]);

  useLayoutEffect(() => () => animationRef.current?.cancel(), []);

  return (
    <span
      ref={elementRef}
      aria-hidden="true"
      className="pointer-events-none absolute top-0 left-[22px] w-[3px] -translate-x-1/2 rounded-[8px] bg-indigo-400"
    />
  );
};

export function computeCallKeyMomentThumb(
  rows: readonly CallKeyMomentRailRow[],
  timestamp: number,
  railHeight: number,
): CallKeyMomentThumb | undefined {
  if (rows.length === 0) return undefined;

  const activeRow = rows.find(
    ({ segment }) =>
      timestamp >= segment.start_timestamp && timestamp < segment.end_timestamp,
  );
  if (activeRow) {
    return {
      top: activeRow.top + THUMB_INSET_PX,
      scale: 1,
      height: Math.max(
        GAP_THUMB_HEIGHT_PX,
        activeRow.height - 2 * THUMB_INSET_PX,
      ),
    };
  }

  const previousRow = rows.findLast(
    ({ segment }) => segment.end_timestamp <= timestamp,
  );
  const nextRow = rows.find(
    ({ segment }) => segment.start_timestamp > timestamp,
  );
  const gapRadius = (GAP_THUMB_HEIGHT_PX * GAP_THUMB_SCALE) / 2;
  const startCenter = previousRow
    ? previousRow.top + previousRow.height - THUMB_INSET_PX
    : gapRadius;
  const endCenter = nextRow
    ? nextRow.top + THUMB_INSET_PX
    : railHeight - THUMB_INSET_PX - gapRadius;
  const center = (startCenter + endCenter) / 2;

  return {
    top:
      Math.max(
        gapRadius,
        Math.min(railHeight - THUMB_INSET_PX - gapRadius, center),
      ) -
      GAP_THUMB_HEIGHT_PX / 2,
    height: GAP_THUMB_HEIGHT_PX,
    scale: GAP_THUMB_SCALE,
  };
}

function buildCallKeyMomentThumbTransition(
  current: CallKeyMomentThumb,
  target: CallKeyMomentThumb,
): CallKeyMomentThumbAnimation {
  const start = renderCallKeyMomentThumbFrame(current);
  const end = renderCallKeyMomentThumbFrame(target);
  if (current.scale === target.scale) {
    return {
      frames: [{ ...start, easing: "ease-out" }, end],
      duration: ANIMATION_STAGE_DURATION_MS,
    };
  }
  const duration = ANIMATION_STAGE_DURATION_MS + ANIMATION_STAGGER_MS;
  const enteringGap = target.scale === GAP_THUMB_SCALE;
  // Partial keyframes let movement and scale run on overlapping timelines.
  const secondStageStart: Keyframe = enteringGap
    ? { transform: start.transform }
    : { top: start.top, height: start.height };
  const firstStageEnd: Keyframe = enteringGap
    ? { top: end.top, height: end.height }
    : { transform: end.transform };
  return {
    frames: [
      { ...start, offset: 0, easing: "ease-out" },
      {
        ...secondStageStart,
        offset: ANIMATION_STAGGER_MS / duration,
        easing: "ease-out",
      },
      {
        ...firstStageEnd,
        offset: ANIMATION_STAGE_DURATION_MS / duration,
        easing: "ease-out",
      },
      { ...end, offset: 1 },
    ],
    duration,
  };
}

function renderCallKeyMomentThumbFrame(frame: CallKeyMomentThumb) {
  return {
    top: `${frame.top}px`,
    transform: `scale(${frame.scale})`,
    height: `${frame.height}px`,
  };
}
